import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Workflow } from "lucide-react";
import { api } from "../lib/api";
import { lensBySlug } from "../data/content";
import DecisionTreeDownload from "../components/DecisionTreeDownload";
import Seo from "../components/Seo";

const SECTIONS = [
  ["What It Is", "what_it_is"],
  ["Why We Care", "why_we_care"],
  ["What We Do", "what_we_do"],
  ["Further Exploration", "further_exploration"],
  ["References", "references"],
];

function parseBold(text, kp) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`${kp}-${i}`} className="text-foreground font-semibold">{p.slice(2, -2)}</strong>
      : <span key={`${kp}-${i}`}>{p}</span>
  );
}

function RichText({ text, mono = false }) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const blocks = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) {
      blocks.push({ type: "ul", items: bullets });
      bullets = [];
    }
  };
  lines.forEach((line) => {
    if (line.startsWith("- ")) bullets.push(line.slice(2));
    else { flush(); blocks.push({ type: "p", text: line }); }
  });
  flush();
  return (
    <div className="space-y-4">
      {blocks.map((b, i) =>
        b.type === "ul" ? (
          <ul key={i} className="space-y-2.5">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-3 text-base text-foreground/75 leading-relaxed">
                <span className="text-primary mt-1.5 shrink-0">▪</span>
                <span>{parseBold(it, `${i}-${j}`)}</span>
              </li>
            ))}
          </ul>
        ) : mono ? (
          <p key={i} className="text-sm text-foreground/55 leading-relaxed break-words">{b.text}</p>
        ) : (
          <p key={i} className="text-base text-foreground/75 leading-relaxed">{parseBold(b.text, i)}</p>
        )
      )}
    </div>
  );
}

// Live download served by the backend (free companion PDF, gated by email capture).
const DECISION_TREE_PAGE_URL = "/resources/cumulative-exposure-decision-tree";

const RESOURCES = {
  "cumulative-exposure-decision-tree": {
    title: "Cumulative Exposure Decision Tree",
    description:
      "A practical worksheet for mapping recurring routines, identifying converging inputs, prioritizing dominant contributors, and selecting systems-level changes.",
    buttonText: "Download the Decision Tree",
  },
};

function ResourceCallout({ id }) {
  const r = RESOURCES[id];
  if (!r) return null;
  return (
    <div data-testid={`resource-${id}`} className="my-8 relative border border-border bg-surface/50">
      {/* blueprint corner ticks */}
      <span aria-hidden className="absolute -top-px -left-px h-3.5 w-3.5 border-t-2 border-l-2 border-primary/60" />
      <span aria-hidden className="absolute -top-px -right-px h-3.5 w-3.5 border-t-2 border-r-2 border-primary/60" />
      <span aria-hidden className="absolute -bottom-px -left-px h-3.5 w-3.5 border-b-2 border-l-2 border-primary/60" />
      <span aria-hidden className="absolute -bottom-px -right-px h-3.5 w-3.5 border-b-2 border-r-2 border-primary/60" />
      <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row sm:items-start gap-5">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-primary/40 text-primary bg-base">
          <Workflow className="h-6 w-6" strokeWidth={1.5} />
        </span>
        <div className="min-w-0">
          <p className="mono-label">Companion worksheet</p>
          <h3 className="mt-2 font-display text-xl font-medium leading-snug">{r.title}</h3>
          <p className="mt-2 text-sm text-foreground/65 leading-relaxed">{r.description}</p>
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <DecisionTreeDownload
              testid={`resource-${id}-download`}
              label={r.buttonText}
              className="inline-flex items-center gap-2 border border-primary text-primary px-5 py-3 font-mono text-[11px] uppercase tracking-widest transition-colors hover:bg-primary hover:text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-base"
            />
            <Link
              to={DECISION_TREE_PAGE_URL}
              data-testid={`resource-${id}-view`}
              className="inline-flex items-center gap-1.5 text-foreground/60 hover:text-primary font-mono text-[11px] uppercase tracking-widest transition-colors"
            >
              View interactive version <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Splits a section on {{RESOURCE:id}} markers and injects the matching callout inline.
function renderSectionContent(content, key) {
  const parts = content.split(/\{\{RESOURCE:([a-z-]+)\}\}/g);
  return parts.map((part, i) => {
    if (i % 2 === 1) return <ResourceCallout key={`res-${i}`} id={part} />;
    const t = part.trim();
    return t ? <RichText key={`txt-${i}`} text={t} mono={key === "references"} /> : null;
  });
}

export default function GreenpaperDetailPage() {
  const { slug } = useParams();
  const [paper, setPaper] = useState(undefined);

  useEffect(() => {
    api.get(`/greenpapers/${slug}`).then((r) => setPaper(r.data)).catch(() => setPaper(null));
  }, [slug]);

  if (paper === undefined) return <div className="mx-auto max-w-3xl px-5 py-32 font-mono text-sm text-muted-foreground">Loading…</div>;
  if (paper === null) return (
    <div data-testid="greenpaper-notfound" className="mx-auto max-w-3xl px-5 py-32 text-center">
      <h1 className="font-display text-3xl">Greenpaper not found</h1>
      <Link to="/greenpapers" className="mt-6 inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest"><ArrowLeft className="h-4 w-4" /> Back to Greenpapers</Link>
    </div>
  );

  const hasBody = SECTIONS.some(([, key]) => (paper[key] || "").trim());

  return (
    <article data-testid="greenpaper-detail" className="mx-auto max-w-[760px] px-5 sm:px-8 py-16">
      <Seo
        title={paper.title}
        description={paper.summary}
        path={`/greenpapers/${paper.slug}`}
        type="article"
        image={paper.image}
        article={{ author: "My Eco Engineer", section: lensBySlug(paper.category)?.title || paper.category }}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: paper.title,
          description: paper.summary,
          articleSection: lensBySlug(paper.category)?.title || paper.category,
          image: paper.image ? [paper.image] : ["https://myecoengineer.com/brand/mee-og.png"],
          author: { "@type": "Organization", name: "My Eco Engineer" },
          publisher: {
            "@type": "Organization",
            name: "My Eco Engineer",
            logo: { "@type": "ImageObject", url: "https://myecoengineer.com/brand/mee-favicon-512.png" },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": `https://myecoengineer.com/greenpapers/${paper.slug}` },
        }}
      />
      <Link to={`/greenpapers/category/${paper.category}`} className="inline-flex items-center gap-2 text-foreground/60 hover:text-primary font-mono text-[11px] uppercase tracking-widest transition-colors">
        <ArrowLeft className="h-4 w-4" /> {lensBySlug(paper.category)?.title || paper.category}
      </Link>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <span className="px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest border" style={{ color: lensBySlug(paper.category)?.color || "#00E599", borderColor: `${lensBySlug(paper.category)?.color || "#00E599"}66` }}>{lensBySlug(paper.category)?.title || paper.category}</span>
        {paper.subcategory && <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">/ {paper.subcategory}</span>}
      </div>

      <h1 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight leading-tight">{paper.title}</h1>
      <p className="mt-6 text-lg text-foreground/85 leading-relaxed border-l-2 pl-5" style={{ borderColor: lensBySlug(paper.category)?.color || "#00E599" }}>{paper.summary}</p>

      {hasBody ? (
        <div className="mt-12 space-y-10">
          {SECTIONS.map(([label, key]) => {
            const content = (paper[key] || "").trim();
            if (!content) return null;
            return (
              <section key={key}>
                <h2 className="font-display text-2xl font-medium tracking-tight mb-4">{label}</h2>
                {renderSectionContent(content, key)}
              </section>
            );
          })}
        </div>
      ) : (
        <div data-testid="greenpaper-pending" className="mt-12 border border-dashed border-border p-8">
          <p className="mono-label">Full analysis publishing soon</p>
          <p className="mt-3 text-sm text-foreground/60 leading-relaxed">
            This Greenpaper is structured around: What It Is, Why We Care, What We Do, Further Exploration, and References.
            The full analysis is being finalized. Subscribe to Greenpapers to be notified when it publishes.
          </p>
          <Link to="/greenpapers" className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary">
            <ArrowLeft className="h-3.5 w-3.5" /> All Greenpapers
          </Link>
        </div>
      )}

      {paper.tags?.length > 0 && (
        <div className="mt-12 border-t border-border pt-6 flex flex-wrap gap-2">
          {paper.tags.map((t) => (
            <span key={t} className="px-2.5 py-1 font-mono text-[10px] text-muted-foreground border border-border">{t}</span>
          ))}
        </div>
      )}

      {/* Related next step, internal linking + conversion */}
      <div className="mt-12 border border-border bg-surface/40 p-7">
        <p className="mono-label">Turn insight into action</p>
        <h3 className="mt-3 font-display text-xl">Ready to apply this to your own home?</h3>
        <p className="mt-2 text-sm text-foreground/60 leading-relaxed">Get the prioritized, room-by-room system in The Low-Tox Home Blueprint, or work with us directly.</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/products/low-tox-home-blueprint" data-testid="gp-cta-blueprint" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary-hover transition-colors">View the Blueprint <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/services" data-testid="gp-cta-services" className="inline-flex items-center gap-2 border border-border px-5 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors">Explore Services</Link>
        </div>
      </div>
    </article>
  );
}
